import {
  YStack,
  SizableText,
  Unspaced,
  Dialog,
  isWeb,
  useDidFinishSSR,
  DialogProps,
  Stack,
  XStackProps,
  XStack,
} from '@mythical/ui'
import { Button } from '../primitive/Button'
import { useRouter } from 'solito/router'
import { IconClose } from '../primitive/Icon'
import { Tag } from '../primitive/Tag/Tag'
import { SolitoImage } from 'solito/image'

/**
 * Upsell Modal shown before user goes to Memberful checkout.
 * Only shown if userStore.canMemberfulLink is true (respects regional restrictions).
 */
export const UpsellModal = (props: DialogProps) => {
  const router = useRouter()
  const didSSR = useDidFinishSSR()
  if (!didSSR) return null

  const handleUpgrade = () => {
    props.onOpenChange?.(false)
    router.push('/perks')
  }

  return (
    <Dialog modal {...props}>
      {/* On web mobile and native always use sheet. Dialogs broken on native */}
      <Dialog.Adapt {...(isWeb ? { when: 'xs' } : { platform: 'touch' })}>
        <Dialog.Sheet modal dismissOnSnapToBottom animation="medium">
          <Dialog.Sheet.Overlay
            bg="$backdrop"
            animation="quick"
            enterStyle={{ o: 0 }}
            exitStyle={{ o: 0 }}
          />
          <Dialog.Sheet.Frame theme="bgSecondary" pt="$2" pb="$2">
            <Dialog.Sheet.Handle bg="$borderSecondary" />
            <Dialog.Sheet.ScrollView>
              <Dialog.Adapt.Contents />
            </Dialog.Sheet.ScrollView>
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          key={`overlay`}
          bg="$backdrop"
          animateOnly={['transform', 'opacity']}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          key="content"
          theme="bgSecondary"
          br="$8"
          p="$0"
          overflow="hidden"
          maxWidth={700}
          w={isWeb ? 'calc(100% - 32px)' : '100%'}
          elevate
          enterStyle={{ x: 0, y: -20, opacity: 0 }}
          exitStyle={{ x: 0, y: 10, opacity: 0 }}
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
        >
          <Unspaced>
            <Dialog.Close asChild>
              <Button
                theme="secondary"
                circular
                size="$2.5"
                icon={<IconClose size={20} />}
                pos="absolute"
                right="$5"
                top="$5"
                zi="$6"
              />
            </Dialog.Close>
          </Unspaced>

          <YStack
            p="$3.5"
            py="$4"
            gap="$6"
            $gtXs={{ px: '$5', py: '$6', gap: '$7' }}
          >
            <YStack ai="flex-start" gap="$3.5" $gtXs={{ px: '$3.5', py: '$6' }}>
              <Tag variant="teal" displaySize="md" uppercase={false}>
                All Access
              </Tag>
              <Dialog.Title
                ff="$display"
                size="$l"
                $gtXs={{ size: '$xl' }}
                tt="uppercase"
              >
                Ready For All Access?
              </Dialog.Title>
              <Dialog.Description size="$2" fow="500">
                Go all-in with premium content, live events, and exclusive
                physical collectibles.
              </Dialog.Description>
            </YStack>

            <YStack gap="$3">
              <PerkItem
                title="20% Mythical Store merch discount"
                image={require('app/assets/images/perks-jumper.png')}
                imageWidth={57}
                imageHeight={62}
                gap={20}
                btw={1}
                bbw={1}
              />
              <PerkItem
                title="Exclusive unique collectible items"
                image={require('app/assets/images/perks-collectible.png')}
                imageWidth={58}
                imageHeight={58}
                gap={15}
                bbw={1}
                pt="$0"
              />
              <PerkItem
                title="Livestreams with Rhett & Link"
                image={require('app/assets/images/perks-rhetlink.png')}
                imageWidth={70}
                imageHeight={50}
                gap={10}
                bbw={1}
                pt="$0"
              />
            </YStack>

            <YStack gap="$3" $gtXs={{ fd: 'row-reverse', p: '$2' }}>
              <Button
                size="$5"
                theme="primary"
                $gtXs={{ fb: '49%', fs: 1, fg: 1 }}
                onPress={handleUpgrade}
              >
                Upgrade to All Access
              </Button>

              <Button
                onPress={() => props.onOpenChange?.(false)}
                theme="secondary"
                size="$5"
                $gtXs={{ fb: '49%', fs: 1, fg: 1 }}
              >
                No thanks
              </Button>
            </YStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

interface PerkItemProps extends XStackProps {
  title: string
  image: string
  imageWidth: number
  imageHeight: number
}

export const PerkItem = ({
  title,
  image,
  imageWidth,
  imageHeight,
  ...props
}: PerkItemProps) => {
  return (
    <XStack gap="$3.5" py="$2" ai="center" boc="$borderSecondary" {...props}>
      <Stack fs={0}>
        <SolitoImage
          src={image}
          width={imageWidth}
          height={imageHeight}
          alt=""
        />
      </Stack>
      <SizableText fs={1} ff="$title" size="$l">
        {title}
      </SizableText>
    </XStack>
  )
}
